"use client";

import { useEffect, useRef, useState } from "react";

type RevealCallback = () => void;

let observer: IntersectionObserver | null = null;
const callbacks = new WeakMap<Element, RevealCallback>();

function getObserver(): IntersectionObserver | null {
  if (typeof window === "undefined") return null;

  if (!observer) {
    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const callback = callbacks.get(entry.target);
          if (callback) callback();
          observer?.unobserve(entry.target);
          callbacks.delete(entry.target);
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -10% 0px" },
    );
  }

  return observer;
}

export function useReveal<T extends HTMLElement>(delay = 0) {
  const ref = useRef<T>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const activeObserver = getObserver();
    if (!activeObserver) {
      setRevealed(true);
      return;
    }

    let timer: number | undefined;

    const callback = () => {
      if (delay > 0) {
        timer = window.setTimeout(() => setRevealed(true), delay);
      } else {
        setRevealed(true);
      }
    };

    callbacks.set(element, callback);
    activeObserver.observe(element);

    return () => {
      if (timer) window.clearTimeout(timer);
      activeObserver.unobserve(element);
      callbacks.delete(element);
    };
  }, [delay]);

  return { ref, revealed };
}
